import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * Read-only display of a resource's universal deep link.
 *
 * Shows the URL in a disabled TextField with two actions:
 * - Open in a new tab (rel="noopener noreferrer nofollow")
 * - Copy to clipboard
 *
 * The value is generated server-side from the resource id, so the input is not
 * editable here. If the url is empty (e.g. a brand-new record that hasn't been
 * saved yet), the actions are disabled and a placeholder is shown.
 */
const UniversalLinkField = ({ url, label = 'Universal link', helperText }) => {
  const [copied, setCopied] = useState(false);
  const hasUrl = Boolean(url);

  const handleCopy = async () => {
    if (!hasUrl) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable — silently ignore; the URL is still visible.
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
      <TextField
        fullWidth
        label={label}
        value={url || ''}
        placeholder={hasUrl ? '' : 'Available after save'}
        helperText={helperText}
        variant="outlined"
        InputProps={{ readOnly: true }}
        inputProps={{ 'aria-readonly': true }}
      />
      <Tooltip title={hasUrl ? 'Open in new tab' : 'Save first to generate link'}>
        <span>
          <IconButton
            aria-label="Open universal link in new tab"
            disabled={!hasUrl}
            component="a"
            href={hasUrl ? url : undefined}
            target="_blank"
            rel="noopener noreferrer nofollow"
            sx={{ mt: 1 }}
          >
            <OpenInNewIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
        <span>
          <IconButton
            aria-label="Copy universal link to clipboard"
            disabled={!hasUrl}
            onClick={handleCopy}
            sx={{ mt: 1 }}
          >
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

UniversalLinkField.propTypes = {
  url: PropTypes.string,
  label: PropTypes.string,
  helperText: PropTypes.string,
};

export default UniversalLinkField;
